import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Calendar as CalendarIcon, User, Stethoscope, Clock, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

export default function CalendarViewPage() {
  const { user } = useAuth();
  const api = useApi();
  
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    loadAppointments();
    loadDoctors();
  }, []);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/appointments');
      setAppointments(response);
    } catch (error) {
      console.error('Failed to load appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response);
    } catch (error) {
      console.error('Failed to load doctors:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#3B82F6',      // blue
      confirmed: '#6366F1',      // indigo
      in_progress: '#EAB308',    // yellow
      completed: '#22C55E',      // green
      cancelled: '#EF4444',      // red
      no_show: '#6B7280',        // gray
      rescheduled: '#A855F7'     // purple
    };
    return colors[status] || colors.scheduled;
  };

  // Filter and transform appointments for calendar
  const events = useMemo(() => {
    let filtered = appointments;

    // Filter by doctor
    if (selectedDoctor !== 'all') {
      filtered = filtered.filter(apt => apt.doctor_id === selectedDoctor);
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(apt => apt.status === selectedStatus);
    }

    // Transform to calendar events
    return filtered.map(apt => {
      const appointmentDate = new Date(apt.appointment_date);
      const [hours, minutes] = apt.appointment_time.split(':');
      const startDate = new Date(appointmentDate);
      startDate.setHours(parseInt(hours), parseInt(minutes), 0);
      
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + 30); // Default 30 min appointments

      return {
        id: apt.id,
        title: `${apt.patient_name} - ${apt.doctor_name.split(' ').slice(0, 2).join(' ')}`,
        start: startDate,
        end: endDate,
        resource: apt,
        style: {
          backgroundColor: getStatusColor(apt.status),
          borderColor: getStatusColor(apt.status)
        }
      };
    });
  }, [appointments, selectedDoctor, selectedStatus]);

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.style.backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '0.875rem',
        padding: '2px 5px'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
  };

  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-purple-900 mb-2">Calendar View</h1>
            <p className="text-gray-600">Visual appointment schedule</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex gap-4 items-center">
              <Filter className="h-5 w-5 text-gray-400" />
              
              <div className="flex-1">
                <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Doctors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Doctors</SelectItem>
                    {doctors.map(doc => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1">
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" onClick={loadAppointments}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-4 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('scheduled') }}></div>
                <span className="text-sm">Scheduled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('confirmed') }}></div>
                <span className="text-sm">Confirmed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('in_progress') }}></div>
                <span className="text-sm">In Progress</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('completed') }}></div>
                <span className="text-sm">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('cancelled') }}></div>
                <span className="text-sm">Cancelled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('no_show') }}></div>
                <span className="text-sm">No Show</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: getStatusColor('rescheduled') }}></div>
                <span className="text-sm">Rescheduled</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <Card>
          <CardContent className="p-4">
            {loading && appointments.length === 0 ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : (
              <div style={{ height: '600px' }}>
                <Calendar
                  localizer={localizer}
                  events={events}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '100%' }}
                  onSelectEvent={handleSelectEvent}
                  eventPropGetter={eventStyleGetter}
                  view={view}
                  onView={handleViewChange}
                  date={date}
                  onNavigate={handleNavigate}
                  views={['month', 'week', 'day', 'agenda']}
                  step={30}
                  showMultiDayTimes
                  defaultDate={new Date()}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Event Details Dialog */}
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Appointment Details</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4 py-4">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-semibold">{selectedEvent.patient_name}</p>
                    <p className="text-sm text-gray-600">{selectedEvent.patient_phone}</p>
                    {selectedEvent.patient_email && (
                      <p className="text-sm text-gray-600">{selectedEvent.patient_email}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Stethoscope className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-semibold">{selectedEvent.doctor_name}</p>
                    {selectedEvent.doctor_nmc_number && (
                      <p className="text-sm text-gray-600">NMC: {selectedEvent.doctor_nmc_number}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-semibold">{moment(selectedEvent.appointment_date).format('MMMM DD, YYYY')}</p>
                    <p className="text-sm text-gray-600">{selectedEvent.appointment_time}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Type:</span>
                  <Badge variant="outline">{selectedEvent.appointment_type}</Badge>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Status:</span>
                  <Badge style={{ backgroundColor: getStatusColor(selectedEvent.status), color: 'white' }}>
                    {selectedEvent.status}
                  </Badge>
                </div>

                {selectedEvent.notes && (
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-sm text-gray-600 font-medium mb-1">Notes:</p>
                    <p className="text-sm">{selectedEvent.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setSelectedEvent(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
